import { format } from 'date-fns';
import { type FC } from 'react';
import { styled } from 'styled-components';

import type { ScanValidatedCustomer } from '@dutchie-pos/requests/checkin/camera-id-scan';

import { DutchieButton, DutchieCancelButton } from 'src/components/dutchie-button';

import { BLOCKER_REASONS, checkInBlocker, createBlocker } from './check-in-eligibility';
import { Container, Prompt } from './step-chrome';

type DecisionStepProps = {
  candidates: ScanValidatedCustomer[];
  createFrom: ScanValidatedCustomer | null;
  /** What the scan read, still shown when creation is withheld because the profile already exists. */
  identified: ScanValidatedCustomer | null;
  features?: Record<string, boolean>;
  isSubmitting: boolean;
  onCheckIn: (customer: ScanValidatedCustomer) => void;
  onCreate: (customer: ScanValidatedCustomer) => void;
  onStartOver: () => void;
};

/**
 * `YYYY-MM-DD`, or the date half of a serialized `DateTime`, at local midnight so no day shifts.
 *
 * Local to this step rather than in the shared contract: these are the only dates this surface
 * renders, and nothing on the wire depends on how they are displayed.
 *
 * The shape alone is not enough. The `Date` constructor rolls a date that does not exist forward,
 * so `1990-02-30` would read back as 2 March and `1990-13-01` as January 1991. These values are a
 * date of birth and an ID expiry, so a silently shifted day is an age or a validity answered
 * wrongly rather than refused — round-tripping the parts is what rejects them.
 */
const toLocalDate = (value?: string | null): Date | null => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(value ?? '');
  if (!parts) {
    return null;
  }

  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(year, month - 1, day);
  const exists = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  return exists ? date : null;
};

const onDate = (value: string | null | undefined): string | null => {
  const date = toLocalDate(value ?? null);

  return date ? format(date, 'MMMM d, yyyy') : null;
};

const joined = (parts: (string | null)[], separator: string): string | null =>
  parts.filter(Boolean).join(separator) || null;

const describe = (customer: ScanValidatedCustomer): string =>
  joined([onDate(customer.DateOfBirth), customer.Status, customer.CustomerType], ' · ') ?? '';

const fullName = (customer: ScanValidatedCustomer): string =>
  customer.FullName ?? [customer.FirstName, customer.LastName].filter(Boolean).join(' ');

/** An expiry is only appended when there is one, so an absent date leaves no dangling separator. */
const withExpiry = (number: string | null, expiry: string | null): string | null => {
  const on = onDate(expiry);

  return joined([number, on && `expires ${on}`], ' · ');
};

/** What the scan says. Drawn from the fields the create payload writes, so approval matches. */
const readDetails = (customer: ScanValidatedCustomer): { label: string; value: string }[] =>
  [
    {
      label: 'Name',
      value: joined(
        [customer.NamePrefix, customer.FirstName, customer.MiddleName, customer.LastName, customer.NameSuffix],
        ' ',
      ),
    },
    { label: 'Date of birth', value: onDate(customer.DateOfBirth) },
    {
      label: "Driver's licence",
      value: withExpiry(customer.DriversLicenseNumber, customer.DriversLicenseExpirationDate),
    },
    { label: 'Medical ID', value: withExpiry(customer.MJStateIdNumber, customer.MJStateIdExpirationDate) },
    {
      label: 'Address',
      value: joined(
        [customer.Address1, customer.Address2, joined([customer.City, customer.State], ', '), customer.PostalCode],
        ', ',
      ),
    },
  ].filter((row): row is { label: string; value: string } => row.value !== null);

export const DecisionStep: FC<DecisionStepProps> = ({
  candidates,
  createFrom,
  identified,
  features,
  isSubmitting,
  onCheckIn,
  onCreate,
  onStartOver,
}) => {
  const createBlockedBy = createFrom ? createBlocker(createFrom, features) : null;

  return (
    <Container>
      <Prompt>
        {candidates.length > 1 ? `${String(candidates.length)} possible matches` : 'Check in this customer'}
      </Prompt>
      {candidates.length === 0 && <Hint>No existing profile matched this ID.</Hint>}
      {/* Always shown: what was read is what staff approve before the one irreversible action here. */}
      {identified && (
        <Details data-testid='check-in_read-details'>
          {readDetails(identified).map((row) => (
            <DetailRow key={row.label}>
              <DetailLabel>{row.label}</DetailLabel>
              <DetailValue data-testid={`check-in_read-detail_${row.label.replace(/[^a-z]/gi, '')}`}>
                {row.value}
              </DetailValue>
            </DetailRow>
          ))}
        </Details>
      )}
      {candidates.map((candidate) => {
        const blockedBy = checkInBlocker(candidate, features);

        return (
          <Candidate key={candidate.Id} data-testid={`check-in_candidate_${String(candidate.Id)}`}>
            <DutchieButton
              automationId={`check-in_candidate-action_${String(candidate.Id)}`}
              ddActionName='Check in matched customer (check in)'
              disabled={isSubmitting || blockedBy !== null}
              fullWidth
              secondary
              onClick={() => onCheckIn(candidate)}
            >
              {fullName(candidate)}
            </DutchieButton>
            <Meta>{describe(candidate)}</Meta>
            {blockedBy && (
              <Blocked data-testid={`check-in_blocked_${String(candidate.Id)}`}>{BLOCKER_REASONS[blockedBy]}</Blocked>
            )}
          </Candidate>
        );
      })}
      {createFrom && (
        <>
          {candidates.length > 0 && <Hint>None of these is the customer?</Hint>}
          <DutchieButton
            automationId='check-in_create'
            cta
            ddActionName='Create profile and check in (check in)'
            disabled={isSubmitting || createBlockedBy !== null}
            onClick={() => onCreate(createFrom)}
          >
            {isSubmitting ? 'Checking in' : 'Create a profile and check in'}
          </DutchieButton>
          {createBlockedBy && (
            <Blocked data-testid='check-in_create-blocked'>{BLOCKER_REASONS[createBlockedBy]}</Blocked>
          )}
        </>
      )}
      <DutchieCancelButton
        automationId='check-in_start-over'
        ddActionName='Start over (check in)'
        fullWidth
        onClick={onStartOver}
      >
        Scan a different ID
      </DutchieCancelButton>
    </Container>
  );
};

const Hint = styled.p`
  color: var(--color-gray-70);
  font-size: 0.875rem;
  margin: 0;
`;

const Details = styled.dl`
  background-color: var(--color-gray-10, #f7f8f9);
  border: 1px solid var(--color-gray-30, #e1e4e8);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0.875rem 1rem;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const DetailLabel = styled.dt`
  color: var(--color-gray-70);
  font-size: 0.75rem;
  text-transform: uppercase;
`;

const DetailValue = styled.dd`
  color: black;
  font-size: 0.9375rem;
  margin: 0;
`;

const Candidate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Meta = styled.span`
  color: var(--color-gray-70);
  font-size: 0.8125rem;
`;

const Blocked = styled.span`
  color: var(--color-red-60);
  font-size: 0.8125rem;
`;
